<template>
  <div class="broadcaster-page" :class="{ embedded: embedded }">
    <div class="container">
      <h2 v-if="!$props.embedded" class="page-title">主播中心</h2>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 非主播 - 申请成为主播 -->
        <div v-if="!isAnchor" class="apply-card">
          <div class="apply-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#ff9600" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          </div>
          <h3 class="apply-title">成为主播，开启直播之旅</h3>
          <p class="apply-desc">通过实名认证并提交申请，即可开始直播</p>
          <button class="apply-btn" @click="goAnchorApply">前往申请主播</button>
        </div>

        <!-- 是主播但没有直播间 -->
        <div v-else-if="!roomCreated" class="create-room-card">
          <h3 class="section-title">创建直播间</h3>
          <div class="form-group">
            <label>房间标题</label>
            <input
              v-model="roomForm.title"
              type="text"
              placeholder="输入直播间标题"
              maxlength="50"
            >
          </div>
          <div class="form-group">
            <label>直播封面</label>
            <label class="cover-uploader" :class="{ uploading: uploadingCover === 'room' }">
              <img v-if="roomForm.cover" :src="previewUrl(roomForm.cover)" alt="直播封面预览">
              <span v-else>{{ uploadingCover === 'room' ? '上传中...' : '点击上传 16:9 封面' }}</span>
              <input type="file" accept="image/jpeg,image/png,image/webp" @change="handleCoverUpload('room', $event)">
            </label>
            <p class="field-tip">支持 JPG、PNG、WebP，建议 1280×720，最大 5MB</p>
          </div>
          <div class="form-group">
            <label>房间简介</label>
            <textarea
              v-model="roomForm.description"
              placeholder="简介一下你的直播间"
              rows="3"
            ></textarea>
          </div>
          <button
            class="submit-btn"
            :disabled="creatingRoom"
            @click="createRoom"
          >{{ creatingRoom ? '创建中...' : '创建直播间' }}</button>
        </div>

        <!-- 主播控制台 -->
        <div v-else class="dashboard">
          <!-- 直播间信息卡片 -->
          <div class="room-info-card">
            <div class="room-info-header">
              <img
                :src="previewUrl(roomInfo.cover)"
                class="room-cover"
                alt="封面"
              >
              <div class="room-info-text">
                <h3 class="room-title">{{ roomInfo.title || '未命名直播间' }}</h3>
                <span
                  class="room-status"
                  :class="{ live: roomInfo.is_live, waiting: roomInfo.is_waiting }"
                >
                  <span class="status-dot" :class="{ live: roomInfo.is_live, waiting: roomInfo.is_waiting }"></span>
                  {{ roomInfo.status_text || (roomInfo.is_live ? '直播中' : '未开播') }}
                </span>
              </div>
            </div>
            <div class="room-stats">
              <div class="stat-item">
                <span class="stat-num">{{ roomInfo.view_count || 0 }}</span>
                <span class="stat-lbl">观看数</span>
              </div>
              <div class="stat-item">
                <span class="stat-num">{{ roomInfo.like_count || 0 }}</span>
                <span class="stat-lbl">点赞数</span>
              </div>
            </div>
          </div>

          <!-- 未开播 - 开播设置 -->
          <div v-if="!roomInfo.is_live && !roomInfo.is_waiting" class="live-control-card">
            <h3 class="section-title">开始直播</h3>
            <div class="form-group">
              <label>直播标题</label>
              <input v-model="liveForm.title" type="text" placeholder="输入本次直播标题">
            </div>
            <div class="form-group">
              <label>本场直播封面</label>
              <label class="cover-uploader compact" :class="{ uploading: uploadingCover === 'live' }">
                <img :src="previewUrl(liveForm.cover || roomInfo.cover)" alt="本场直播封面">
                <span>{{ uploadingCover === 'live' ? '上传中...' : '点击更换封面' }}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" @change="handleCoverUpload('live', $event)">
              </label>
            </div>
            <button
              class="submit-btn live"
              :disabled="startingLive"
              @click="startLive"
            >{{ startingLive ? '开播中...' : '开始直播' }}</button>
          </div>

          <!-- 已点开播，等待 OBS 真正推流 -->
          <div v-else-if="roomInfo.is_waiting" class="live-waiting-card">
            <div class="waiting-title">
              <span class="waiting-dot"></span>
              等待 OBS 推流
            </div>
            <p>请把下方 RTMP 地址和串流密钥填入 OBS。检测到有效推流后，页面会自动切换为“直播中”。</p>
            <button class="stop-btn" :disabled="stoppingLive" @click="stopLive">
              {{ stoppingLive ? '取消中...' : '取消本次开播' }}
            </button>
          </div>

          <!-- 直播中 - 控制 -->
          <div v-else class="live-active-card">
            <div class="live-badge">
              <span class="live-dot"></span>
              直播中 · 已开播 {{ liveDuration }} 分钟
            </div>
            <div class="live-real-time">
              <div class="rt-item">
                <span class="rt-num">{{ liveStats.view_count || 0 }}</span>
                <span class="rt-lbl">实时观看</span>
              </div>
              <div class="rt-item">
                <span class="rt-num">{{ liveStats.like_count || 0 }}</span>
                <span class="rt-lbl">点赞</span>
              </div>
              <div class="rt-item">
                <span class="rt-num">{{ liveStats.gift_amount || 0 }}</span>
                <span class="rt-lbl">礼物收入</span>
              </div>
            </div>
            <button
              class="stop-btn"
              :disabled="stoppingLive"
              @click="stopLive"
            >{{ stoppingLive ? '关闭中...' : '停止直播' }}</button>
          </div>

          <!-- 推流信息 -->
          <div class="stream-info-card">
            <div class="stream-card-heading">
              <h3 class="section-title">推流信息</h3>
              <div class="stream-actions">
                <a href="https://obsproject.com/download" target="_blank" rel="noopener noreferrer">下载 OBS</a>
                <a href="#/help?tab=live">查看直播教程</a>
                <a href="#/user?tab=roomadmin">房管设置</a>
              </div>
            </div>
            <p class="stream-hint">使用 OBS 等推流工具推流到以下地址</p>
            <div class="stream-field">
              <label>RTMP 地址</label>
              <div class="stream-value-wrap">
                <span class="stream-value">{{ streamInfo.rtmp_url || '--' }}</span>
                <button class="copy-btn" @click="copyText(streamInfo.rtmp_url)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  <span>复制</span>
                </button>
              </div>
            </div>
            <div class="stream-field">
              <label>串流密钥 (Stream Key)</label>
              <div class="stream-value-wrap">
                <span class="stream-value">{{ streamInfo.stream_key || '--' }}</span>
                <button class="copy-btn" @click="copyText(streamInfo.stream_key)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  <span>复制</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <PageFooter v-if="!$props.embedded" />
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue';
import PageFooter from './Footer.vue';
import { getAnchorApplyStatus } from '../api/anchor';
import * as liveRoomApi from '../api/liveRoom';
import { uploadFile } from '../api/user';
import { BASE_URL } from '../api/config';

export default {
  name: 'UserBroadcaster',
  components: { PageFooter },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    void props.embedded;
    const loading = ref(false);
    const isAnchor = ref(false);
    const roomCreated = ref(false);
    const roomInfo = ref({});
    const liveForm = reactive({ title: '', cover: '' });
    const roomForm = reactive({ title: '', cover: '', description: '' });
    const startingLive = ref(false);
    const stoppingLive = ref(false);
    const creatingRoom = ref(false);
    const uploadingCover = ref('');
    const liveDuration = ref(0);
    const liveStats = ref({});
    const streamInfo = ref({});
    const defaultCover = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="68" viewBox="0 0 16 9" fill="%23eee"%3E%3Crect width="16" height="9" /%3E%3C/svg%3E';

    let durationTimer = null;
    let statusTimer = null;

    const fetchAnchorStatus = async () => {
      loading.value = true;
      try {
        const res = await getAnchorApplyStatus();
        if (res.code === 1) {
          const data = res.data;
          isAnchor.value = !!data.is_anchor;
          if (isAnchor.value) {
            await fetchRoomInfo();
          }
        }
      } catch (e) {
        console.error('获取主播状态失败', e);
      } finally {
        loading.value = false;
      }
    };

    const fetchRoomInfo = async () => {
      try {
        const res = await liveRoomApi.getMyRoom();
        if (res.code === 1) {
          const data = res.data;
          roomCreated.value = !!data.room;
          if (data.room) {
            roomInfo.value = data.room;
            liveStats.value = {
              view_count: data.room.view_count || 0,
              like_count: data.room.like_count || 0,
              gift_amount: data.room.gift_amount || 0
            };
            streamInfo.value = data.stream_info || data.room.stream_info || {};
            if (data.room.is_live) {
              if (data.room.live_start_time) {
                const start = Number(data.room.live_start_time);
                if (start > 0) {
                  liveDuration.value = Math.floor((Date.now() / 1000 - start) / 60);
                }
              }
              startDurationTimer();
            } else {
              stopDurationTimer();
            }
          }
        }
      } catch (e) {
        console.error('获取直播间信息失败', e);
      }
    };

    const goAnchorApply = () => {
      window.location.hash = '#/user?tab=anchor';
    };

    const previewUrl = (value) => {
      const source = String(value || '').trim();
      if (!source) return defaultCover;
      if (/^(https?:|data:|blob:)/i.test(source)) return source;
      return BASE_URL + (source.startsWith('/') ? source : '/' + source);
    };

    const handleCoverUpload = async (target, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        alert('封面仅支持 JPG、PNG 或 WebP 图片');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('封面图片不能超过 5MB');
        return;
      }
      uploadingCover.value = target;
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await uploadFile(formData);
        if (response.data?.code !== 1) throw new Error(response.data?.msg || '上传失败');
        const url = response.data.data?.url || response.data.data?.fullurl || '';
        if (!url) throw new Error('上传结果缺少文件地址');
        if (target === 'room') roomForm.cover = url;
        else liveForm.cover = url;
      } catch (e) {
        alert(e.message || '封面上传失败，请重试');
      } finally {
        uploadingCover.value = '';
      }
    };

    const createRoom = async () => {
      if (!roomForm.title.trim()) {
        alert('请输入房间标题');
        return;
      }
      creatingRoom.value = true;
      try {
        const res = await liveRoomApi.createLiveRoom({
          title: roomForm.title.trim(),
          cover: roomForm.cover.trim(),
          description: roomForm.description.trim()
        });
        if (res.code === 1) {
          alert('直播间创建成功！');
          await fetchRoomInfo();
        } else {
          alert(res.msg || '创建失败');
        }
      } catch (e) {
        alert('创建失败，请重试');
      } finally {
        creatingRoom.value = false;
      }
    };

    const startLive = async () => {
      if (!liveForm.title.trim()) {
        alert('请输入直播标题');
        return;
      }
      startingLive.value = true;
      try {
        const res = await liveRoomApi.startLive(roomInfo.value.id, {
          title: liveForm.title.trim(),
          cover: liveForm.cover.trim() || roomInfo.value.cover || '',
          description: roomInfo.value.description || ''
        });
        if (res.code === 1) {
          roomInfo.value = res.data.room;
          streamInfo.value = res.data.stream_info || streamInfo.value;
          liveDuration.value = 0;
          alert(res.msg || '已准备开播，请启动 OBS 推流');
        } else {
          alert(res.msg || '开播失败');
        }
      } catch (e) {
        alert('开播失败，请重试');
      } finally {
        startingLive.value = false;
      }
    };

    const stopLive = async () => {
      if (!confirm('确定要停止直播吗？')) return;
      stoppingLive.value = true;
      try {
        const res = await liveRoomApi.stopLive(roomInfo.value.id);
        if (res.code === 1) {
          roomInfo.value.is_live = 0;
          stopDurationTimer();
          await fetchRoomInfo();
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        alert('停止直播失败，请重试');
      } finally {
        stoppingLive.value = false;
      }
    };

    const startDurationTimer = () => {
      stopDurationTimer();
      durationTimer = setInterval(() => {
        liveDuration.value++;
      }, 60000);
    };

    const stopDurationTimer = () => {
      if (durationTimer) {
        clearInterval(durationTimer);
        durationTimer = null;
      }
    };

    const startStatusPolling = () => {
      stopStatusPolling();
      statusTimer = setInterval(fetchRoomInfo, 2000);
    };

    const stopStatusPolling = () => {
      if (statusTimer) {
        clearInterval(statusTimer);
        statusTimer = null;
      }
    };

    const copyText = (text) => {
      if (!text) {
        alert('没有可复制的信息');
        return;
      }
      navigator.clipboard.writeText(text).then(() => {
        alert('已复制到剪贴板');
      }).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        alert('已复制到剪贴板');
      });
    };

    onMounted(async () => {
      await fetchAnchorStatus();
      startStatusPolling();
    });
    onUnmounted(() => {
      stopDurationTimer();
      stopStatusPolling();
    });

    return {
      loading,
      isAnchor,
      roomCreated,
      roomInfo,
      roomForm,
      liveForm,
      startingLive,
      stoppingLive,
      creatingRoom,
      uploadingCover,
      liveDuration,
      liveStats,
      streamInfo,
      defaultCover,
      goAnchorApply,
      previewUrl,
      handleCoverUpload,
      createRoom,
      startLive,
      stopLive,
      copyText
    };
  }
};
</script>

<style scoped>
.broadcaster-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f6f7f9 0%, #f0f2f5 100%);
  padding: 40px 0;
}
.broadcaster-page.embedded {
  min-height: auto;
  padding: 0;
  background: transparent;
}
.broadcaster-page.embedded .container {
  max-width: none;
  margin: 0;
  padding: 0;
}
.container {
  flex: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}
.page-title {
  font-size: 24px;
  color: #1a1a2e;
  margin-bottom: 24px;
  font-weight: 700;
  position: relative;
  padding-left: 16px;
}
.page-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
  border-radius: 2px;
}
.section-title {
  font-size: 18px;
  color: #1a1a2e;
  margin: 0 0 16px;
  font-weight: 700;
}

/* 申请主播 */
.apply-card {
  background: #fff;
  border-radius: 16px;
  padding: 60px 30px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.apply-icon {
  margin-bottom: 20px;
  opacity: 0.8;
}
.apply-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
}
.apply-desc {
  font-size: 14px;
  color: #999;
  margin: 0 0 32px;
}
.apply-btn {
  padding: 14px 48px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(255,106,0,0.2);
}
.apply-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255,106,0,0.3);
}

/* 创建直播间 */
.create-room-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.form-group {
  margin-bottom: 18px;
}
.form-group label {
  display: block;
  margin-bottom: 6px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #f8fafc;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  transition: all 0.3s;
}
.form-group input:focus,
.form-group textarea:focus {
  border-color: #ff9600;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(255,150,0,0.08);
}
.cover-uploader {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 260px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b !important;
  cursor: pointer;
}
.cover-uploader:hover { border-color: #ff9600; background: #fff8f0; }
.cover-uploader.uploading { pointer-events: none; opacity: .7; }
.cover-uploader img { width: 100%; height: 100%; object-fit: cover; }
.cover-uploader span { position: absolute; padding: 8px 14px; border-radius: 18px; background: rgba(15,23,42,.72); color: #fff; }
.cover-uploader input { display: none; }
.cover-uploader.compact { max-width: 360px; max-height: 202px; }
.field-tip { margin: 7px 0 0; color: #94a3b8; font-size: 12px; }
.submit-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(255,106,0,0.2);
}
.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}
.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255,106,0,0.3);
}
.submit-btn.live {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}
.submit-btn.live:hover:not(:disabled) {
  box-shadow: 0 4px 14px rgba(239,68,68,0.3);
}

/* 控制台 - 直播间信息 */
.room-info-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.room-info-header {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.room-cover {
  width: 120px;
  height: 68px;
  border-radius: 10px;
  object-fit: cover;
  background: #f0f0f0;
  flex-shrink: 0;
}
.room-info-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}
.room-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}
.room-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 14px;
  background: #f1f5f9;
  color: #94a3b8;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  align-self: flex-start;
}
.room-status.live {
  background: #fef2f2;
  color: #ef4444;
}
.room-status.waiting {
  background: #fff7ed;
  color: #ea580c;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #94a3b8;
}
.status-dot.live {
  background: #ef4444;
  animation: pulse 1.5s infinite;
}
.status-dot.waiting {
  background: #f97316;
  animation: pulse 1.2s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.room-stats {
  display: flex;
  gap: 40px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
}
.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.stat-lbl {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

/* 开播控制 */
.live-control-card,
.live-active-card,
.live-waiting-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.live-waiting-card {
  border-color: #fed7aa;
  background: linear-gradient(135deg, #fff 0%, #fff7ed 100%);
}
.waiting-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #c2410c;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}
.waiting-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f97316;
  animation: pulse 1.2s infinite;
}
.live-waiting-card p {
  margin: 0 0 18px;
  color: #7c2d12;
  line-height: 1.7;
}
.live-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ef4444;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
}
.live-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ef4444;
  animation: pulse 1.5s infinite;
}
.live-real-time {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
}
.rt-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}
.rt-num {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.rt-lbl {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}
.stop-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(239,68,68,0.2);
}
.stop-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}
.stop-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(239,68,68,0.3);
}

/* 推流信息 */
.stream-info-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.02);
}
.stream-card-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
.stream-actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.stream-actions a { color: #ea580c; font-size: 13px; text-decoration: none; font-weight: 600; }
.stream-actions a:hover { text-decoration: underline; }
.stream-hint {
  font-size: 13px;
  color: #999;
  margin: -8px 0 16px;
}
.stream-field {
  margin-bottom: 14px;
}
.stream-field label {
  display: block;
  font-size: 13px;
  color: #475569;
  margin-bottom: 6px;
  font-weight: 500;
}
.stream-value-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stream-value {
  flex: 1;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #1a1a2e;
  font-family: monospace;
  word-break: break-all;
}
.copy-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #ff9600;
  color: #ff9600;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}
.copy-btn:hover {
  background: #ff9600;
  color: #fff;
  box-shadow: 0 2px 6px rgba(255,150,0,0.2);
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: #999;
  font-size: 14px;
}
.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #ff9600;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
